/* Ops = the action machinery shared across surfaces: toasts, the confirm
   dialog every guarded action must pass through, and the append-only audit
   trail. Ports the prototype's pushToast / askConfirm / doConfirm and the
   "prepend an audit row" pattern — but the row is also POSTed to /api/audit
   (D1-backed, append-only) so it survives reload. Optimistic rows are exposed
   so the Controls audit table shows an action the instant it's taken. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Dialog, Button, Toast } from '../ds';
import type { AuditRow } from '../lib/contract';

export type ToastTone = 'success' | 'info' | 'warn' | 'danger';
interface ToastItem { id: string; tone: ToastTone; title: string; msg?: string }

/** An audit row as callers supply it — result defaults to 'ok', time to 'now'. */
export type AuditInput = Omit<AuditRow, 'time' | 'result'> & { time?: string; result?: AuditRow['result'] };

export interface ConfirmConfig {
  title: string;
  body: string;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  auditAction: string;
  auditTarget: string;
  toastTitle: string;
  toastMsg?: string;
  toastTone?: ToastTone;
  /** Optional real executor (deploy hook, health-check, run-agent…). Runs
      before the audit row is finalized; a throw marks the row result 'error'. */
  run?: () => Promise<void>;
}

interface Ops {
  pushToast: (tone: ToastTone, title: string, msg?: string) => void;
  askConfirm: (cfg: ConfirmConfig) => void;
  appendAudit: (row: AuditInput) => void;
  optimisticAudit: AuditRow[];
}

const OpsCtx = createContext<Ops | null>(null);
let seq = 0;
const nextId = () => `t${Date.now().toString(36)}-${seq++}`;

async function postAudit(row: AuditRow): Promise<void> {
  try {
    await fetch('/api/audit', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(row),
    });
  } catch {
    /* offline / functions not running — the optimistic row still shows. */
  }
}

export function OpsProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirm, setConfirm] = useState<ConfirmConfig | null>(null);
  const [optimisticAudit, setOptimisticAudit] = useState<AuditRow[]>([]);
  const [busy, setBusy] = useState(false);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const opener = useRef<HTMLElement | null>(null);

  const dismiss = useCallback((id: string) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
    const h = timers.current[id];
    if (h) {
      clearTimeout(h);
      delete timers.current[id];
    }
  }, []);

  const pushToast = useCallback(
    (tone: ToastTone, title: string, msg?: string) => {
      const id = nextId();
      setToasts((ts) => [...ts, { id, tone, title, msg }]);
      timers.current[id] = setTimeout(() => dismiss(id), 4000);
    },
    [dismiss],
  );

  const appendAudit = useCallback(
    (row: AuditInput) => {
      const full: AuditRow = { actor: row.actor, action: row.action, target: row.target, result: row.result ?? 'ok', time: row.time ?? 'now' };
      setOptimisticAudit((rows) => [full, ...rows]);
      void postAudit(full);
    },
    [],
  );

  const askConfirm = useCallback((cfg: ConfirmConfig) => setConfirm(cfg), []);
  const cancelConfirm = useCallback(() => setConfirm(null), []);

  const doConfirm = useCallback(async () => {
    const c = confirm;
    if (!c) return;
    setBusy(true);
    let result: AuditRow['result'] = 'ok';
    try {
      if (c.run) await c.run();
    } catch {
      result = 'error';
    } finally {
      setBusy(false);
    }
    setConfirm(null);
    appendAudit({ actor: 'Cassidy', action: c.auditAction, target: c.auditTarget, result });
    if (result === 'error') {
      pushToast('danger', 'Action failed', `${c.auditAction} · ${c.auditTarget}`);
    } else {
      pushToast(c.toastTone ?? 'success', c.toastTitle, c.toastMsg);
    }
  }, [confirm, appendAudit, pushToast]);

  // Esc closes the confirm dialog.
  useEffect(() => {
    if (!confirm) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirm, cancelConfirm]);

  // Dialog accessibility: name it (aria-labelledby → its <h3>), move focus in,
  // trap Tab within it, and return focus to the opener on close. The DS Dialog
  // renders role="dialog"; we enhance it from the consumer to avoid patching the
  // vendored bundle.
  const dialogOpen = !!confirm;
  useEffect(() => {
    if (!dialogOpen) return;
    const dialog = document.querySelector<HTMLElement>('[role="dialog"]');
    if (!dialog) return;
    opener.current = document.activeElement as HTMLElement | null;

    const h3 = dialog.querySelector('h3');
    if (h3 && !dialog.getAttribute('aria-labelledby')) {
      if (!h3.id) h3.id = 'mc-dialog-title';
      dialog.setAttribute('aria-labelledby', h3.id);
    }

    const focusables = () =>
      Array.from(dialog.querySelectorAll<HTMLElement>('button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])')).filter(
        (el) => !el.hasAttribute('disabled') && el.offsetParent !== null,
      );

    const items = focusables();
    (items.find((el) => /deploy|run|confirm|toggle|publish|silence|working/i.test(el.textContent ?? '')) ?? items[0])?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      opener.current?.focus();
    };
  }, [dialogOpen]);

  const value = useMemo<Ops>(
    () => ({ pushToast, askConfirm, appendAudit, optimisticAudit }),
    [pushToast, askConfirm, appendAudit, optimisticAudit],
  );

  const danger = confirm?.tone === 'danger';

  return (
    <OpsCtx.Provider value={value}>
      {children}

      <Dialog
        open={!!confirm}
        title={confirm?.title}
        onClose={cancelConfirm}
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={cancelConfirm} disabled={busy}>
              Cancel
            </Button>
            <Button variant={danger ? 'danger' : 'primary'} onClick={doConfirm} disabled={busy}>
              {busy ? 'Working…' : confirm?.confirmLabel ?? 'Confirm'}
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>{confirm?.body}</p>
      </Dialog>

      <div
        aria-live="polite"
        onFocus={() => Object.values(timers.current).forEach(clearTimeout)}
        onBlur={() => toasts.forEach((t) => { clearTimeout(timers.current[t.id]); timers.current[t.id] = setTimeout(() => dismiss(t.id), 4000); })}
        style={{ position: 'fixed', right: 18, bottom: 18, display: 'flex', flexDirection: 'column', gap: 10, zIndex: 'var(--z-toast)' as unknown as number, maxWidth: 360 }}
      >
        {toasts.map((t) => (
          <Toast key={t.id} tone={t.tone} title={t.title} onClose={() => dismiss(t.id)}>
            {t.msg}
          </Toast>
        ))}
      </div>
    </OpsCtx.Provider>
  );
}

export function useOps(): Ops {
  const o = useContext(OpsCtx);
  if (!o) throw new Error('useOps must be used within <OpsProvider>');
  return o;
}
